import { FC, useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Loadable from '@components/Loadable/Loadable';
import {
  IMedicationResponseDetails,
  MedicationFormFieldNames,
  MedicationServerFieldNames,
} from '@interfaces/medications';
import { IUserEnteredSupplementAddNote } from '@interfaces/supplements';
import {
  inquiryResponseActiveMedicationDataSelector,
  inquiryResponseActiveMedicationErrorSelector,
  inquiryResponseActiveMedicationIsLoadingSelector,
} from '@store/features/inquiryResponse/selectors';

import MedicationNeedsInfoForm from './MedicationNeedsInfoForm/MedicationNeedsInfoForm';

interface IProps {
  hasUnseenResponses: boolean;
  isLastOneThatNeedsInfo: boolean;
  onSubmit: (values: IUserEnteredSupplementAddNote) => void;
  onClose: () => void;
  onRetry: () => void;
}

const MedicationNeedsInfo: FC<IProps> = ({
  hasUnseenResponses,
  isLastOneThatNeedsInfo,
  onSubmit,
  onClose,
  onRetry,
}) => {
  const { t } = useTranslation();

  const activeMedication = useSelector(inquiryResponseActiveMedicationDataSelector);
  const activeMedicationIsLoading = useSelector(inquiryResponseActiveMedicationIsLoadingSelector);
  const activeMedicationError = useSelector(inquiryResponseActiveMedicationErrorSelector);

  const initialValues = useMemo(() => {
    const data =
      activeMedication && typeof activeMedication === 'object'
        ? activeMedication
        : ({} as IMedicationResponseDetails);
    return {
      [MedicationFormFieldNames.Name]: data[MedicationServerFieldNames.Name],
      [MedicationFormFieldNames.ProductLink]: data[MedicationServerFieldNames.ProductLink],
      [MedicationFormFieldNames.Comment]: data[MedicationServerFieldNames.Comments],
      [MedicationFormFieldNames.Note]: '',
    };
  }, [activeMedication]);

  const handleValidateNeedsInfo = useCallback((values: any) => {
    const errors: { [key: string]: boolean } = {};
    if (!values[MedicationFormFieldNames.Name]) {
      errors[MedicationFormFieldNames.Name] = true;
    }
    if (!values[MedicationFormFieldNames.Note]) {
      errors[MedicationFormFieldNames.Note] = true;
    }
    return errors;
  }, []);

  return (
    <Loadable
      isLoading={activeMedicationIsLoading}
      error={activeMedicationError && t('errors.userEnteredMedicationError')}
      onRetry={onRetry}
    >
      <Form
        // @ts-ignore
        component={MedicationNeedsInfoForm}
        adminComments={activeMedication?.comments || ''}
        hasUnseenResponses={hasUnseenResponses}
        isLastOneThatNeedsInfo={isLastOneThatNeedsInfo}
        initialValues={initialValues}
        validate={handleValidateNeedsInfo}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Loadable>
  );
};

export default MedicationNeedsInfo;
